import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Shield, Users, CheckCircle, Award, TrendingUp, Heart, Eye, Handshake, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../components/LanguageSelector';
import BackButton from '../components/BackButton';

export default function InnovationSocialePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const chiffres = [
    { valeur: "600+", label: t('innovation.stats.secured_sites', 'Chantiers sécurisés'), icon: Shield },
    { valeur: "0", label: t('innovation.stats.damages', 'Dommages enregistrés'), icon: CheckCircle },
    { valeur: "200+", label: t('innovation.stats.professionals', 'Professionnels accompagnés'), icon: Users },
    { valeur: "10", label: t('innovation.stats.guarantee_years', 'Années de garantie'), icon: Award }
  ];

  const principes = [
    { numero: "01", titre: t('innovation.principles.membership', 'Adhésion'), description: t('innovation.principles.membership_desc', 'Le professionnel rejoint le dispositif coopératif COPPAM') },
    { numero: "02", titre: t('innovation.principles.contribution', 'Cotisation solidaire'), description: t('innovation.principles.contribution_desc', 'Contribution mutualisée adaptée aux réalités territoriales') },
    { numero: "03", titre: t('innovation.principles.declaration', 'Déclaration chantier'), description: t('innovation.principles.declaration_desc', 'Chaque chantier est identifié et suivi') },
    { numero: "04", titre: t('innovation.principles.support', 'Accompagnement'), description: t('innovation.principles.support_desc', 'Prévention, expertise et soutien technique') },
    { numero: "05", titre: t('innovation.principles.guarantee', 'Garantie 10 ans'), description: t('innovation.principles.guarantee_desc', 'Sécurisation civile conforme au Code civil') }
  ];

  const valeurs = [
    { icon: Heart, titre: t('innovation.values.solidarity', 'Solidarité'), description: t('innovation.values.solidarity_desc', "L'entraide collective au service de chacun") },
    { icon: Users, titre: t('innovation.values.mutualization', 'Mutualisation'), description: t('innovation.values.mutualization_desc', 'Partager les ressources pour renforcer tous') },
    { icon: Eye, titre: t('innovation.values.transparency', 'Transparence'), description: t('innovation.values.transparency_desc', 'Gestion claire et responsable des fonds') },
    { icon: Shield, titre: t('innovation.values.prevention', 'Prévention'), description: t('innovation.values.prevention_desc', 'Anticiper pour mieux protéger') },
    { icon: Handshake, titre: t('innovation.values.equity', 'Équité'), description: t('innovation.values.equity_desc', 'Un accès juste pour tous les professionnels') },
    { icon: TrendingUp, titre: t('innovation.values.sustainability', 'Durabilité'), description: t('innovation.values.sustainability_desc', 'Un modèle pérenne et responsable') }
  ];

  const territoires = [
    "Guadeloupe", "Marie-Galante", "Les Saintes", "La Désirade",
    "Martinique", "Guyane", "Saint-Martin", "Saint-Barthélemy",
    "La Réunion", "Mayotte", "Nouvelle-Calédonie"
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
      {/* Language Selector - Fixed position */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector variant="compact" />
      </div>
      
      {/* Hero Section avec image de fond */}
      <header className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Image de fond - Présentation COPPAM */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url("https://customer-assets.emergentagent.com/job_oscop-admin/artifacts/6r1hkoie_PRESENTATION%20COPPAM%20vierge.png")`
          }}
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/85 to-[#0a1628]/60" />
        
        <div className="relative z-10 w-full px-6 md:px-[10%] py-20">
          <div className="mb-8">
            <BackButton variant="dark" label={t('common.back', 'Retour')} />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Contenu gauche */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c8a24d]/20 rounded-full mb-6">
                <span className="w-2 h-2 bg-[#c8a24d] rounded-full animate-pulse" />
                <span className="text-[#c8a24d] text-sm font-medium uppercase tracking-wider">
                  {t('innovation.title', 'Innovation Sociale')} • COPPAM
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                {t('innovation.pass_title', 'Le Pass RCD')}<br/>
                <span className="text-[#c8a24d]">{t('innovation.pass_subtitle', 'Solidaire au Chantier')}</span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 max-w-xl leading-relaxed">
                {t('innovation.hero_subtitle', 'Un dispositif coopératif unique né des territoires ultramarins, fondé sur la solidarité entre professionnels du bâtiment et la sécurisation des chantiers.')}
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  className="bg-[#c8a24d] hover:bg-[#b8923d] text-[#0a1628] font-bold px-4 md:px-8"
                  onClick={() => navigate('/contact')}
                >
                  {t('innovation.cta.join', 'Nous rejoindre')}
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-4 md:px-8"
                  onClick={() => navigate('/garanties-solidaires-btp')}
                >
                  Découvrir nos Garanties solidaires
                </Button>
              </div>
            </div>

            {/* Badge 10 ANS Premium - Version améliorée */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Effets lumineux externes */}
                <div className="absolute inset-0 bg-[#c8a24d]/20 rounded-full blur-3xl scale-150 animate-pulse"/>
                <div className="absolute inset-0 bg-[#c8a24d]/10 rounded-full blur-2xl scale-125"/>
                
                {/* Cercles orbitaux animés */}
                <div className="absolute inset-[-20px] border-2 border-[#c8a24d]/20 rounded-full animate-spin" style={{animationDuration: '20s'}}/>
                <div className="absolute inset-[-35px] border border-[#c8a24d]/10 rounded-full animate-spin" style={{animationDuration: '30s', animationDirection: 'reverse'}}/>
                
                {/* Points lumineux sur les orbites */}
                <div className="absolute inset-[-20px] animate-spin" style={{animationDuration: '20s'}}>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#c8a24d] rounded-full shadow-lg shadow-[#c8a24d]/50"/>
                </div>
                <div className="absolute inset-[-35px] animate-spin" style={{animationDuration: '30s', animationDirection: 'reverse'}}>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#c8a24d]/60 rounded-full"/>
                </div>
                
                {/* Badge principal avec bordure premium */}
                <div className="relative">
                  {/* Bordure externe dorée avec gradient */}
                  <div className="absolute inset-[-4px] bg-gradient-to-br from-[#c8a24d] via-[#f5e6a3] to-[#c8a24d] rounded-full animate-pulse" style={{animationDuration: '3s'}}/>
                  
                  {/* Bordure interne avec motif */}
                  <div className="absolute inset-[-2px] bg-gradient-to-br from-[#0a1628] to-[#122a4a] rounded-full"/>
                  
                  {/* Corps du badge */}
                  <div className="relative bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#0a1628] rounded-full w-52 h-52 md:w-60 md:h-60 flex flex-col items-center justify-center text-center shadow-2xl overflow-hidden">
                    {/* Effet de brillance */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent"/>
                    
                    {/* Cercle décoratif intérieur */}
                    <div className="absolute inset-4 border border-[#c8a24d]/30 rounded-full"/>
                    <div className="absolute inset-8 border border-[#c8a24d]/20 rounded-full"/>
                    
                    {/* Étoiles décoratives */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 text-[#c8a24d] fill-[#c8a24d]" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                        </svg>
                      ))}
                    </div>
                    
                    {/* Icône trophée/award */}
                    <div className="relative mb-1">
                      <Award className="w-12 h-12 md:w-14 md:h-14 text-[#c8a24d] drop-shadow-lg"/>
                      <div className="absolute inset-0 animate-ping">
                        <Award className="w-12 h-12 md:w-14 md:h-14 text-[#c8a24d]/30"/>
                      </div>
                    </div>
                    
                    {/* Nombre 10 avec effet */}
                    <div className="relative">
                      <span className="text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-b from-[#f5e6a3] via-[#c8a24d] to-[#a67c00] drop-shadow-lg">
                        10
                      </span>
                    </div>
                    
                    {/* YEARS */}
                    <div className="text-lg md:text-xl font-bold text-white uppercase tracking-[0.3em] mt-1">
                      Years
                    </div>
                    
                    {/* Guarantee */}
                    <div className="text-[10px] md:text-xs text-[#c8a24d] uppercase tracking-widest mt-1 font-semibold">
                      Guarantee
                    </div>
                    
                    {/* Ligne décorative */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-20 h-0.5 bg-gradient-to-r from-transparent via-[#c8a24d]/50 to-transparent"/>
                  </div>
                </div>
                
                {/* Ruban "2015-2025" amélioré */}
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
                  <div className="relative">
                    {/* Ombre du ruban */}
                    <div className="absolute inset-0 bg-black/30 blur-md translate-y-1"/>
                    {/* Ruban principal */}
                    <div className="relative bg-gradient-to-r from-[#a67c00] via-[#c8a24d] to-[#a67c00] text-[#0a1628] px-6 py-2 rounded-full text-sm font-black shadow-xl border border-[#f5e6a3]/30">
                      <span className="drop-shadow-sm">2015 — 2025</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Chiffres clés */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {chiffres.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
                  <Icon className="w-8 h-8 text-[#c8a24d] mx-auto mb-3" />
                  <div className="text-3xl md:text-4xl font-bold text-white">{item.valeur}</div>
                  <div className="text-gray-400 text-sm mt-1">{item.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Section Principes */}
      <section className="py-20 px-6 md:px-[10%] bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#c8a24d] font-semibold uppercase tracking-wider">{t('innovation.principles.title', 'Comment ça marche')}</span>
            <h2 className="text-4xl font-bold text-[#0a1628] mt-3">{t('innovation.principles.device_title', 'Principes du dispositif')}</h2>
          </div>

          <div className="grid md:grid-cols-5 gap-4">
            {principes.map((principe, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow h-full border-t-4 border-[#c8a24d]">
                  <div className="text-4xl font-bold text-[#c8a24d]/20 mb-3">{principe.numero}</div>
                  <h4 className="text-lg font-bold text-[#0a1628] mb-2">{principe.titre}</h4>
                  <p className="text-gray-600 text-sm">{principe.description}</p>
                </div>
                {index < principes.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-0.5 bg-[#c8a24d]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Professionnels Ultramarins */}
      <section className="py-20 px-6 md:px-[10%]">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#c8a24d] font-semibold uppercase tracking-wider">{t('innovation.professionals.subtitle', 'Au cœur du dispositif')}</span>
              <h2 className="text-4xl font-bold text-[#0a1628] mt-3 mb-6">
                {t('innovation.professionals.title', 'Les professionnels ultramarins')}
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                La RCDS Nominative est née de la réalité quotidienne des artisans, 
                chefs d'entreprise et compagnons exerçant dans les territoires ultramarins, 
                confrontés à des contraintes économiques, climatiques et structurelles spécifiques.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                COPPAM s'est construite avec eux, au plus près des chantiers, 
                en valorisant l'expérience, la responsabilité individuelle 
                et la solidarité collective.
              </p>

              {/* Territoires */}
              <div className="flex flex-wrap gap-2">
                {territoires.map((territoire, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-[#0a1628]/5 text-[#0a1628] rounded-full text-sm"
                  >
                    <MapPin className="w-3 h-3 text-[#c8a24d]" />
                    {territoire}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              {/* Carte stylisée des territoires connectés - Version dynamique */}
              <div className="bg-gradient-to-br from-[#0a1628] via-[#122a4a] to-[#1a3a5c] rounded-2xl p-8 shadow-2xl overflow-hidden">
                {/* Effet de brillance animé */}
                <div className="absolute top-0 left-0 w-full h-full">
                  <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse" style={{animation: 'shimmer 3s infinite'}}/>
                </div>
                
                <div className="text-center mb-8 relative z-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c8a24d]/20 rounded-full mb-3">
                    <div className="w-2 h-2 bg-[#c8a24d] rounded-full animate-ping"/>
                    <span className="text-[#c8a24d] text-xs font-semibold uppercase tracking-wider">{t('innovation.territories.active_network', 'Réseau Actif')}</span>
                  </div>
                  <h3 className="text-white font-bold text-2xl mb-2">{t('innovation.territories.title', 'Nos Territoires Connectés')}</h3>
                  <p className="text-gray-400 text-sm">{t('innovation.territories.subtitle', 'Un réseau solidaire couvrant tous les Outre-mer français')}</p>
                </div>
                
                {/* Visualisation des territoires améliorée */}
                <div className="relative h-72">
                  {/* Cercles concentriques animés */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 border border-[#c8a24d]/20 rounded-full animate-pulse"/>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border border-[#c8a24d]/10 rounded-full"/>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-[#c8a24d]/5 rounded-full"/>
                  
                  {/* Lignes de connexion SVG animées */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 280">
                    <defs>
                      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#c8a24d" stopOpacity="0.2"/>
                        <stop offset="50%" stopColor="#c8a24d" stopOpacity="0.8"/>
                        <stop offset="100%" stopColor="#c8a24d" stopOpacity="0.2"/>
                      </linearGradient>
                    </defs>
                    {/* Lignes animées vers le centre */}
                    <line x1="60" y1="50" x2="200" y2="140" stroke="url(#lineGradient)" strokeWidth="2">
                      <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="2s" repeatCount="indefinite"/>
                    </line>
                    <line x1="340" y1="50" x2="200" y2="140" stroke="url(#lineGradient)" strokeWidth="2">
                      <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="2.5s" repeatCount="indefinite"/>
                    </line>
                    <line x1="60" y1="230" x2="200" y2="140" stroke="url(#lineGradient)" strokeWidth="2">
                      <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="2.2s" repeatCount="indefinite"/>
                    </line>
                    <line x1="340" y1="230" x2="200" y2="140" stroke="url(#lineGradient)" strokeWidth="2">
                      <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="2.8s" repeatCount="indefinite"/>
                    </line>
                    <line x1="60" y1="140" x2="200" y2="140" stroke="url(#lineGradient)" strokeWidth="2">
                      <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="1.8s" repeatCount="indefinite"/>
                    </line>
                    <line x1="340" y1="140" x2="200" y2="140" stroke="url(#lineGradient)" strokeWidth="2">
                      <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="2.3s" repeatCount="indefinite"/>
                    </line>
                  </svg>
                  
                  {/* Point central - COPPAM avec animation */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-20">
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#c8a24d] rounded-full blur-xl opacity-50 animate-pulse"/>
                      <div className="relative w-16 h-16 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] rounded-full flex items-center justify-center shadow-xl">
                        <Shield className="w-8 h-8 text-[#0a1628]"/>
                      </div>
                    </div>
                    <span className="text-[#c8a24d] text-sm font-bold mt-2 block">COPPAM</span>
                  </div>
                  
                  {/* Territoires Caraïbes */}
                  <div className="absolute left-[8%] top-[12%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-5 h-5 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] rounded-full mx-auto mb-1 shadow-lg shadow-[#c8a24d]/30 group-hover:shadow-[#c8a24d]/60"/>
                    <span className="text-white text-xs font-semibold opacity-90 group-hover:opacity-100">Guadeloupe</span>
                  </div>
                  
                  <div className="absolute left-[5%] top-[45%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-4 h-4 bg-[#c8a24d]/80 rounded-full mx-auto mb-1"/>
                    <span className="text-gray-300 text-[10px] group-hover:text-white">Marie-Galante</span>
                  </div>
                  
                  <div className="absolute left-[12%] top-[78%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-5 h-5 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] rounded-full mx-auto mb-1 shadow-lg shadow-[#c8a24d]/30"/>
                    <span className="text-white text-xs font-semibold">Martinique</span>
                  </div>
                  
                  <div className="absolute left-[25%] top-[25%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-4 h-4 bg-[#c8a24d]/80 rounded-full mx-auto mb-1"/>
                    <span className="text-gray-300 text-[10px] group-hover:text-white">St-Martin</span>
                  </div>
                  
                  <div className="absolute left-[28%] top-[65%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-5 h-5 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] rounded-full mx-auto mb-1 shadow-lg shadow-[#c8a24d]/30"/>
                    <span className="text-white text-xs font-semibold">Guyane</span>
                  </div>
                  
                  {/* Océan Indien */}
                  <div className="absolute right-[8%] top-[12%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-5 h-5 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] rounded-full mx-auto mb-1 shadow-lg shadow-[#c8a24d]/30"/>
                    <span className="text-white text-xs font-semibold">La Réunion</span>
                  </div>
                  
                  <div className="absolute right-[5%] top-[45%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-5 h-5 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] rounded-full mx-auto mb-1 shadow-lg shadow-[#c8a24d]/30"/>
                    <span className="text-white text-xs font-semibold">Mayotte</span>
                  </div>
                  
                  {/* Pacifique */}
                  <div className="absolute right-[12%] top-[78%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-5 h-5 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] rounded-full mx-auto mb-1 shadow-lg shadow-[#c8a24d]/30"/>
                    <span className="text-white text-xs font-semibold">Nlle-Calédonie</span>
                  </div>
                  
                  <div className="absolute right-[25%] top-[25%] text-center group cursor-pointer transform hover:scale-110 transition-transform">
                    <div className="w-4 h-4 bg-[#c8a24d]/80 rounded-full mx-auto mb-1"/>
                    <span className="text-gray-300 text-[10px] group-hover:text-white">St-Barthélemy</span>
                  </div>
                </div>
                
                {/* Stats en bas */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#c8a24d]">11</div>
                    <div className="text-gray-400 text-xs">Territoires</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#c8a24d]">3</div>
                    <div className="text-gray-400 text-xs">Océans</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#c8a24d]">1</div>
                    <div className="text-gray-400 text-xs">Réseau solidaire</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-[#c8a24d] to-[#e8d48a] text-[#0a1628] p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">100%</div>
                <div className="text-sm font-medium">Couverture Outre-mer</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Valeur Ajoutée */}
      <section className="py-20 px-6 md:px-[10%] bg-[#0a1628]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#c8a24d] font-semibold uppercase tracking-wider">{t('innovation.added_value.subtitle', 'Notre engagement')}</span>
            <h2 className="text-4xl font-bold text-white mt-3">{t('innovation.added_value.title', 'Valeur ajoutée')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_oscop-admin/artifacts/ygvanja7_PRESENTATION%20COPPAM%20vierge%20%281%29.png"
                alt="Collaboration et partenariat COPPAM"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            
            <div>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Nous promouvons un modèle fondé sur la <strong className="text-white">mutualisation</strong>, 
                la <strong className="text-white">prévention</strong> et la <strong className="text-white">transparence financière</strong>, 
                aligné avec les valeurs de l'économie sociale et solidaire.
              </p>

              {/* Citation */}
              <div className="bg-white/5 border-l-4 border-[#c8a24d] p-6 rounded-r-xl mb-8">
                <p className="text-white italic text-lg">
                  "La garantie n'est plus un produit standardisé, mais un engagement collectif 
                  fondé sur la coopération, la transparence et la responsabilité partagée."
                </p>
              </div>

              <ul className="space-y-4">
                {["Simplification administrative et financière", "Gestion coopérative et durable", "Renforcement de la confiance"].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-[#c8a24d]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section Valeurs */}
      <section className="py-20 px-6 md:px-[10%] bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#c8a24d] font-semibold uppercase tracking-wider">Ce qui nous anime</span>
            <h2 className="text-4xl font-bold text-[#0a1628] mt-3">Nos valeurs</h2>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {valeurs.map((valeur, index) => {
              const Icon = valeur.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all hover:-translate-y-1 text-center group">
                  <div className="w-14 h-14 bg-[#c8a24d]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#c8a24d]/20 transition-colors">
                    <Icon className="w-7 h-7 text-[#c8a24d]" />
                  </div>
                  <h4 className="font-bold text-[#0a1628] mb-2">{valeur.titre}</h4>
                  <p className="text-gray-600 text-xs">{valeur.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Dispositif Solidaire RCDS */}
      <section className="py-20 px-6 md:px-[10%]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c8a24d]/10 rounded-full mb-6">
            <Shield className="w-4 h-4 text-[#c8a24d]" />
            <span className="text-[#c8a24d] text-sm font-medium uppercase tracking-wider">
              Notre dispositif
            </span>
          </div>

          <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
            Dispositif Solidaire RCDS<br/>"Nominative"
          </h2>

          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto leading-relaxed">
            Une aide à la solidité des ouvrages conforme aux articles 1792 et suivants du Code civil, 
            destinée aux professionnels du bâtiment des territoires ultramarins.
          </p>

          <div className="bg-gradient-to-r from-[#c8a24d] to-[#e8d48a] p-1 rounded-2xl">
            <div className="bg-white rounded-xl p-4 md:p-8">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-[#c8a24d]">10 ans</div>
                  <div className="text-gray-600 mt-1">de garantie</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#c8a24d]">100%</div>
                  <div className="text-gray-600 mt-1">conforme au droit civil</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#c8a24d]">0€</div>
                  <div className="text-gray-600 mt-1">de dommage enregistré</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 md:px-[10%] bg-gradient-to-r from-[#c8a24d] to-[#e8d48a]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
            Rejoignez la solidarité coopérative
          </h2>
          <p className="text-[#0a1628]/80 text-lg mb-8 max-w-2xl mx-auto">
            Depuis 10 ans, nous prouvons que l'économie sociale et solidaire 
            est une réponse efficace aux défis du bâtiment ultramarin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white font-bold px-4 md:px-8"
              onClick={() => navigate('/contact')}
            >
              Nous contacter
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white px-4 md:px-8"
              onClick={() => navigate('/register')}
            >
              Devenir membre
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 text-center bg-[#0a1628]">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[#c8a24d]" />
          <span className="text-white font-semibold">COPPAM – Garanties Solidaires</span>
        </div>
        <p className="text-gray-400 text-sm">
          RCDS Nominative – Innovation sociale coopérative née des territoires ultramarins
        </p>
      </footer>
    </div>
  );
}
